import React from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Button from "app/popleads/components/ui-kit/Button";
import EmptyStatus from "app/popleads/components/ui-kit/EmptyStatus";
import { canAskAssistant } from "app/popleads/components/Chat/assistantAccess";
import {
    askAboutError,
    canAskAboutError,
    crashOf,
    errorContext,
    recordRuntimeError,
} from "app/popleads/components/Chat/assistantErrorCapture";
import { ASSISTANT_BUSY } from "app/popleads/components/Chat/assistantErrorToast";

import styles from "./ScreenErrorBoundary.module.scss";

export const ScreenCrashed = ({ crash }) => {
    const { user } = useSelector((state) => state.auth);
    const canAsk = canAskAssistant(user) && canAskAboutError();
    return (
        <div className={styles.crashed} role="alert">
            <EmptyStatus
                title="This screen ran into a problem"
                description="Reload the page to try again. If it keeps happening, the assistant can tell you more."
            />
            <div className={styles.actions}>
                <Button variant="primary" size="medium" onClick={() => window.location.reload()}>
                    Reload the page
                </Button>
                {canAsk && (
                    <Button
                        variant="primary"
                        buttonStyle="option"
                        size="medium"
                        onClick={() => askAboutError(errorContext({ crash })) || toast.info(ASSISTANT_BUSY)}
                    >
                        Ask the assistant
                    </Button>
                )}
            </div>
        </div>
    );
};

/**
 * What a page that throws while rendering shows instead of nothing. Without it a render error
 * unmounts the whole tree and leaves a blank white page — the one error the server never sees,
 * since no request failed.
 *
 * It wraps the page content only, inside the layout: the subheader, and with it the assistant's
 * window, stays on screen, which is what lets "Ask the assistant" work from here. Moving to another
 * page (`resetKey`) gives the next one a clean start.
 */
class ScreenErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { crash: null };
    }

    static getDerivedStateFromError(error) {
        return { crash: crashOf(error) };
    }

    componentDidCatch(error, info) {
        recordRuntimeError(error, info?.componentStack);
        /* The component stack arrives only here, after the fallback has rendered once. */
        this.setState({ crash: crashOf(error, info?.componentStack) });
    }

    componentDidUpdate(prevProps) {
        if (this.state.crash && prevProps.resetKey !== this.props.resetKey) this.setState({ crash: null });
    }

    render() {
        return this.state.crash ? <ScreenCrashed crash={this.state.crash} /> : this.props.children;
    }
}

export default ScreenErrorBoundary;
